// ═══════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE FORMATOS TSS
// Permite ver y editar los campos de cada tipo de planilla.
// Cuando TSS cambia el formato, edita aquí sin tocar código.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Settings, RefreshCw, Save, AlertCircle, CheckCircle,
  ChevronUp, ChevronDown, Upload, Info, Trash2, Plus,
} from 'lucide-react';
import { getTSSFormatos, updateTSSFormato } from '../../../../api/tss';

const TIPOS_LABEL = {
  AM: { label: 'AM — Autodeterminación Mensual', color: 'blue'   },
  RT: { label: 'RT — Rectificativa',             color: 'amber'  },
  BN: { label: 'BN — Bonificación INFOTEP',      color: 'green'  },
  DA: { label: 'DA — Dependientes Adicionales',  color: 'purple' },
};

const COLOR_MAP = {
  blue:   'bg-blue-50 border-blue-200 text-blue-800',
  amber:  'bg-amber-50 border-amber-200 text-amber-800',
  green:  'bg-green-50 border-green-200 text-green-800',
  purple: 'bg-purple-50 border-purple-200 text-purple-800',
};

// Campos internos disponibles para mapear (data context completo)
const CAMPOS_DISPONIBLES = [
  { value: 'clave_nomina',                      label: 'Código Nómina (clave_nomina)' },
  { value: 'tipo_doc',                          label: 'Tipo Documento (tipo_doc)' },
  { value: 'numero_doc_limpio',                 label: 'Número Documento — solo dígitos' },
  { value: 'nombres_upper',                     label: 'Nombres — mayúsculas' },
  { value: 'primer_apellido_upper',             label: '1er Apellido — mayúsculas' },
  { value: 'segundo_apellido_upper',            label: '2do Apellido — mayúsculas' },
  { value: 'sexo',                              label: 'Sexo (M/F)' },
  { value: 'fecha_nacimiento',                  label: 'Fecha Nacimiento (→ DDMMAAAA)' },
  { value: 'salario_cotizable',                 label: 'Salario Cotizable SDSS' },
  { value: 'aporte_voluntario',                 label: 'Aporte Voluntario' },
  { value: 'salario_isr',                       label: 'Salario ISR' },
  { value: 'tipo_ingreso',                      label: 'Tipo Ingreso (N/E/etc.)' },
  { value: 'otras_remuneraciones',              label: 'Otras Remuneraciones' },
  { value: 'rnc_agente_ret',                   label: 'RNC/Cédula Agente Retenedor' },
  { value: 'remuneracion_otros_agentes',        label: 'Remuneración Otros Agentes' },
  { value: 'saldo_a_favor',                    label: 'Saldo a Favor' },
  { value: 'regalia_pascual',                  label: 'Regalía Pascual' },
  { value: 'preaviso_cesantia',                label: 'Preaviso / Cesantía' },
  { value: 'retencion_pension_alimenticia',     label: 'Pensión Alimenticia' },
  { value: 'salario_infotep',                  label: 'Salario Base INFOTEP' },
  { value: 'tipo_trabajador',                  label: 'Tipo Trabajador (C/etc.)' },
  { value: 'monto_bonificacion',               label: 'Monto Bonificación' },
  { value: 'clave_nomina_titular',             label: 'Código Nómina Titular (DA)' },
  { value: 'tipo_doc_titular',                 label: 'Tipo Documento Titular (DA)' },
  { value: 'nro_doc_titular',                  label: 'Nro. Documento Titular (DA)' },
  { value: 'tipo_novedad',                     label: 'Tipo Novedad ID/SD (DA)' },
  { value: 'tipo_doc_dep',                     label: 'Tipo Documento Dependiente (DA)' },
  { value: 'nro_doc_dep',                      label: 'Nro. Documento Dependiente (DA)' },
  { value: 'nombres_dep_upper',                label: 'Nombres Dependiente — mayúsculas (DA)' },
  { value: 'primer_apellido_dep_upper',        label: '1er Apellido Dependiente — mayúsculas (DA)' },
  { value: 'segundo_apellido_dep_upper',       label: '2do Apellido Dependiente — mayúsculas (DA)' },
];

const TIPOS_CAMPO = ['texto', 'monto', 'fecha'];

function CampoRow({ campo, idx, total, onChange, onDelete, onMoveUp, onMoveDown }) {
  return (
    <div className="flex items-center gap-2 p-2.5 bg-white border border-gray-200 rounded-lg group">
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <button
          onClick={onMoveUp}
          disabled={idx === 0}
          className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
        >
          <ChevronUp size={12} />
        </button>
        <button
          onClick={onMoveDown}
          disabled={idx === total - 1}
          className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
        >
          <ChevronDown size={12} />
        </button>
      </div>
      <span className="text-xs text-gray-400 w-5 text-center flex-shrink-0">{idx + 1}</span>

      {/* Nombre TSS */}
      <input
        type="text"
        value={campo.nombre}
        onChange={e => onChange({ ...campo, nombre: e.target.value })}
        placeholder="NOMBRE_CAMPO"
        className="flex-1 px-2 py-1.5 text-xs font-mono border border-gray-200 rounded focus:outline-none focus:border-blue-400 min-w-0"
      />

      {/* Campo interno */}
      <select
        value={campo.campo_interno}
        onChange={e => onChange({ ...campo, campo_interno: e.target.value })}
        className="flex-[2] px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400 min-w-0"
      >
        <option value="">— selecciona campo interno —</option>
        {CAMPOS_DISPONIBLES.map(c => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>

      {/* Tipo */}
      <select
        value={campo.tipo}
        onChange={e => onChange({ ...campo, tipo: e.target.value })}
        className="w-24 px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400"
      >
        {TIPOS_CAMPO.map(t => <option key={t} value={t}>{t}</option>)}
      </select>

      {/* Default */}
      <input
        type="text"
        value={campo.default || ''}
        onChange={e => onChange({ ...campo, default: e.target.value })}
        placeholder="default"
        className="w-20 px-2 py-1.5 text-xs font-mono border border-gray-200 rounded focus:outline-none focus:border-blue-400"
      />

      <button
        onClick={onDelete}
        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

export default function FormatosConfig({ companyId }) {
  const [formatos,    setFormatos]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [activeTipo,  setActiveTipo]  = useState('AM');
  const [campos,      setCampos]      = useState([]);
  const [version,     setVersion]     = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState(null);
  const fileRef = useRef(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getTSSFormatos(companyId);
      setFormatos(res.data || []);
    } catch {
      setError('Error al cargar configuraciones de formatos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [companyId]); // eslint-disable-line

  // Cuando cambia tipo activo, cargar sus campos
  useEffect(() => {
    const fmt = formatos.find(f => f.tipo_archivo === activeTipo);
    if (fmt) {
      const sorted = [...(fmt.campos || [])].sort((a, b) => a.orden - b.orden);
      setCampos(sorted);
      setVersion(fmt.version || '');
      setDescripcion(fmt.descripcion || '');
    } else {
      setCampos([]);
      setVersion('');
      setDescripcion('');
    }
  }, [activeTipo, formatos]);

  const handleCampoChange = (idx, updated) => {
    setCampos(prev => prev.map((c, i) => i === idx ? { ...updated, orden: i + 1 } : c));
  };

  const handleDelete = (idx) => {
    setCampos(prev => prev.filter((_, i) => i !== idx).map((c, i) => ({ ...c, orden: i + 1 })));
  };

  const handleMoveUp = (idx) => {
    if (idx === 0) return;
    setCampos(prev => {
      const arr = [...prev];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return arr.map((c, i) => ({ ...c, orden: i + 1 }));
    });
  };

  const handleMoveDown = (idx) => {
    setCampos(prev => {
      if (idx >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr.map((c, i) => ({ ...c, orden: i + 1 }));
    });
  };

  const handleAddCampo = () => {
    setCampos(prev => [...prev, {
      orden:         prev.length + 1,
      nombre:        '',
      campo_interno: '',
      tipo:          'texto',
      default:       '',
    }]);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updateTSSFormato(companyId, activeTipo, campos, version, descripcion);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Detectar columnas desde Excel (.xlsx/.xls) o TXT pipe-separated
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(null);

    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'xlsx' || ext === 'xls') {
      // ── Excel: leer la primera fila como cabecera de columnas ──
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const wb    = XLSX.read(ev.target.result, { type: 'array' });
          const ws    = wb.Sheets[wb.SheetNames[0]];
          const data  = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

          // Buscar la primera fila que tenga contenido real (puede ser fila 0 o 1)
          const headerRow = data.find(row =>
            row.some(cell => String(cell).trim().replace(/[\[\]]/g, '') !== '')
          );
          if (!headerRow) {
            setError('No se encontraron columnas en el Excel.');
            return;
          }

          const fields = headerRow
            .map(cell => String(cell).trim().replace(/[\[\]]/g, '').toUpperCase())
            .filter(name => name !== '')
            .map((nombre, i) => ({
              orden:         i + 1,
              nombre,
              campo_interno: '',
              tipo:          'texto',
              default:       '',
            }));

          setCampos(fields);
          setError(null);
        } catch {
          setError('No se pudo leer el Excel. Verifica que sea un archivo .xlsx o .xls válido.');
        }
      };
      reader.readAsArrayBuffer(file);

    } else {
      // ── TXT pipe-separated: segunda línea contiene los campos ──
      const reader = new FileReader();
      reader.onload = (ev) => {
        const lines = ev.target.result.split(/\r?\n/).filter(l => l.trim());
        // La primera línea es la cabecera (AM|RNC|...), la segunda es un registro de ejemplo
        const srcLine = lines.length >= 2 ? lines[1] : lines[0];
        if (!srcLine) {
          setError('El archivo TXT está vacío o no tiene el formato esperado.');
          return;
        }
        const fields = srcLine.split('|').map((f, i) => ({
          orden:         i + 1,
          nombre:        f.trim().replace(/[\[\]]/g, '').toUpperCase(),
          campo_interno: '',
          tipo:          'texto',
          default:       '',
        }));
        setCampos(fields);
      };
      reader.readAsText(file);
    }

    e.target.value = '';
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <RefreshCw size={16} className="animate-spin mr-2" /> Cargando formatos...
    </div>
  );

  const tipoInfo = TIPOS_LABEL[activeTipo] || {};
  const colorClass = COLOR_MAP[tipoInfo.color] || '';

  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className={`border rounded-xl p-4 flex items-start gap-3 ${colorClass}`}>
        <Info size={16} className="flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold mb-1">Motor de formatos configurable</p>
          <p className="text-xs opacity-80">
            Si la TSS actualiza el formato de una planilla, sube el nuevo <strong>Excel (.xlsx)</strong> que
            te envíen y el sistema detecta las columnas automáticamente. También acepta un <strong>TXT de
            ejemplo</strong> pipe-separado. Luego solo tienes que mapear cada columna al campo interno
            correspondiente y guardar — sin tocar código.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {saved && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <CheckCircle size={14} /> Formato guardado exitosamente.
        </div>
      )}

      {/* Selector de tipo */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(TIPOS_LABEL).map(([tipo, info]) => (
          <button
            key={tipo}
            onClick={() => setActiveTipo(tipo)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
              activeTipo === tipo
                ? `${COLOR_MAP[info.color]} border-current`
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            {info.label}
          </button>
        ))}
      </div>

      {/* Editor de campos */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Settings size={15} className="text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">
              Campos — {activeTipo} ({campos.length} columnas)
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Versión */}
            <input
              type="text"
              value={version}
              onChange={e => setVersion(e.target.value)}
              placeholder="versión"
              className="w-24 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400"
            />
            {/* Importar TXT */}
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              title="Sube el Excel de formato que provee la TSS, o un TXT de ejemplo"
            >
              <Upload size={12} /> Importar Excel / TXT
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.txt" className="hidden" onChange={handleFileUpload} />
          </div>
        </div>

        {/* Descripción */}
        <div className="px-4 py-2 border-b border-gray-100">
          <input
            type="text"
            value={descripcion}
            onChange={e => setDescripcion(e.target.value)}
            placeholder="Descripción del formato (ej: Autodeterminación Mensual v2024)"
            className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400"
          />
        </div>

        {/* Cabecera de columnas */}
        <div className="flex items-center gap-2 px-4 py-1.5 bg-gray-50 text-xs text-gray-500 border-b border-gray-100">
          <span className="w-10 flex-shrink-0 text-center">Orden</span>
          <span className="flex-1">Nombre en TXT (TSS)</span>
          <span className="flex-[2]">Campo interno del sistema</span>
          <span className="w-24">Tipo</span>
          <span className="w-20">Default</span>
          <span className="w-8"></span>
        </div>

        {/* Rows */}
        <div className="p-3 space-y-1.5 max-h-[32rem] overflow-y-auto">
          {campos.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">
              Sin campos configurados. Agrega uno o importa un TXT de ejemplo.
            </p>
          )}
          {campos.map((campo, idx) => (
            <CampoRow
              key={idx}
              campo={campo}
              idx={idx}
              total={campos.length}
              onChange={(updated) => handleCampoChange(idx, updated)}
              onDelete={() => handleDelete(idx)}
              onMoveUp={() => handleMoveUp(idx)}
              onMoveDown={() => handleMoveDown(idx)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleAddCampo}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800"
          >
            <Plus size={13} /> Agregar campo
          </button>
          <button
            onClick={handleSave}
            disabled={saving || campos.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving
              ? <><RefreshCw size={13} className="animate-spin" /> Guardando...</>
              : <><Save size={13} /> Guardar formato</>
            }
          </button>
        </div>
      </div>

      {/* Vista previa del header TXT */}
      {campos.length > 0 && (
        <div className="bg-gray-900 text-green-400 rounded-xl p-4 font-mono text-xs overflow-x-auto">
          <p className="text-gray-500 mb-1">// Cabecera TXT que se generará:</p>
          <p>{activeTipo}|RNC|MMAAAA|N_EMPLEADOS</p>
          <p className="text-gray-500 mt-2 mb-1">// Línea de empleado (campos en orden):</p>
          <p className="break-all">{campos.map(c => `[${c.nombre || c.campo_interno || '?'}]`).join('|')}</p>
        </div>
      )}
    </div>
  );
}
